import React, { useEffect, useRef } from "react";
import { FormikErrors, getIn } from "formik";

interface ScrollToErrorProps {
    errors: FormikErrors<any>;
    submitClickedCount: number;
    refs?: Record<string, React.RefObject<HTMLElement>>;
    focusDelay?: number;
}

export const useScrollToErrorMeMenu = ({
    errors,
    submitClickedCount,
    refs = {},
    focusDelay = 200,
}: ScrollToErrorProps) => {
    const prevCountRef = useRef(submitClickedCount);
    const prevErrorsRef = useRef<FormikErrors<any>>({});

    useEffect(() => {
        const scrollAndFocus = (element: HTMLElement) => {
            const yOffset = -100;
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.scrollY;
            const scrollToY = absoluteElementTop + yOffset;

            window.scrollTo({
                top: scrollToY,
                behavior: "smooth",
            });

            const focusable =
                (element.querySelector("input, select, textarea, [tabindex]") as HTMLElement | null) || element;

            if (focusable && typeof focusable.focus === "function") {
                setTimeout(() => {
                    focusable.focus({ preventScroll: true });
                }, focusDelay);
            }
        };

        const hasErrors = errors && Object.keys(errors).length > 0;
        const hadErrorsBefore = prevErrorsRef.current && Object.keys(prevErrorsRef.current).length > 0;

        const countChanged = submitClickedCount !== prevCountRef.current;

        const shouldScroll =
            (countChanged && hasErrors) || (!countChanged && !hadErrorsBefore && hasErrors && submitClickedCount > 0);

        if (shouldScroll) {
            const formElements = Array.from(
                document.querySelectorAll("input[name], select[name], textarea[name], [data-error-field]")
            ) as HTMLElement[];

            const firstErrorElement = formElements.find((element) => {
                const name = element.getAttribute("name") || element.getAttribute("data-error-field");
                if (!name) return false;

                const errorForField = getIn(errors, name);
                return Boolean(errorForField);
            });

            const errorFields = Object.keys(errors);
            let elementToScroll = firstErrorElement;

            for (const fieldName of errorFields) {
                const refElement = refs[fieldName]?.current;
                if (refElement) {
                    elementToScroll = refElement;
                    break;
                }
            }

            if (elementToScroll) {
                setTimeout(() => {
                    scrollAndFocus(elementToScroll as HTMLElement);
                }, focusDelay);
            }
        }

        prevCountRef.current = submitClickedCount;
        prevErrorsRef.current = errors;
    }, [submitClickedCount, errors, refs, focusDelay]);
};
